import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import BookDemoModal from '../components/organisms/BookDemoModal';

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const toggleAllFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const pricingPlans = [
    {
      name: 'Starter',
      description: 'Perfect for small teams',
      monthlyPrice: 0,
      annualPrice: 0,
      badge: null,
      mainFeatures: [
        { text: '5 AI interviews per month', included: true },
        { text: 'Basic candidate reports', included: true },
        { text: 'Email support', included: true },
        { text: 'Single user access', included: true },
      ],
      additionalFeatures: [
        { text: '7-day data retention', included: true },
        { text: 'Standard interview templates', included: true },
        { text: 'Advanced analytics', included: false },
        { text: 'Custom branding', included: false },
        { text: 'API access', included: false },
      ],
      cta: 'Get Started Free',
      ctaVariant: 'outline',
      popular: false,
    },
    {
      name: 'Professional',
      description: 'For growing teams',
      monthlyPrice: 4999,
      annualPrice: 49990,
      badge: 'Most Popular',
      mainFeatures: [
        { text: '50 AI interviews per month', included: true },
        { text: 'Comprehensive PDF reports', included: true },
        { text: 'Priority email & chat support', included: true },
        { text: 'Up to 5 team members', included: true },
      ],
      additionalFeatures: [
        { text: '90-day data retention', included: true },
        { text: 'Custom interview templates', included: true },
        { text: 'Advanced analytics dashboard', included: true },
        { text: 'Video interview recordings', included: true },
        { text: 'Candidate comparison tools', included: true },
        { text: 'Custom branding', included: false },
        { text: 'API access', included: false },
        { text: 'Dedicated account manager', included: false },
      ],
      cta: 'Start Free Trial',
      ctaVariant: 'primary',
      popular: true,
    },
    {
      name: 'Business',
      description: 'For established companies',
      monthlyPrice: 14999,
      annualPrice: 149990,
      badge: null,
      mainFeatures: [
        { text: '200 AI interviews per month', included: true },
        { text: 'Advanced PDF reports', included: true },
        { text: '24/7 priority support', included: true },
        { text: 'Up to 20 team members', included: true },
      ],
      additionalFeatures: [
        { text: 'Unlimited data retention', included: true },
        { text: 'Unlimited custom templates', included: true },
        { text: 'Advanced analytics & AI insights', included: true },
        { text: 'Video recordings & transcripts', included: true },
        { text: 'Advanced candidate scoring', included: true },
        { text: 'Full custom branding', included: true },
        { text: 'REST API access', included: true },
        { text: 'ATS integrations', included: true },
        { text: 'Dedicated account manager', included: false },
      ],
      cta: 'Start Free Trial',
      ctaVariant: 'primary',
      popular: false,
    },
    {
      name: 'Enterprise',
      description: 'For large organizations',
      monthlyPrice: null,
      annualPrice: null,
      badge: null,
      mainFeatures: [
        { text: 'Unlimited AI interviews', included: true },
        { text: 'Custom report templates', included: true },
        { text: 'Dedicated support team', included: true },
        { text: 'Unlimited team members', included: true },
      ],
      additionalFeatures: [
        { text: 'Unlimited data retention', included: true },
        { text: 'White-label solution', included: true },
        { text: 'Custom AI model training', included: true },
        { text: 'Advanced security features', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'On-premise deployment option', included: true },
        { text: 'Full API access', included: true },
        { text: 'SLA guarantee', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom onboarding & training', included: true },
      ],
      cta: 'Contact Sales',
      ctaVariant: 'outline',
      popular: false,
    },
  ];

  const formatPrice = (price) => {
    if (price === null) return 'Custom';
    if (price === 0) return 'Free';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getPrice = (plan) => {
    if (plan.monthlyPrice === null) return 'Custom';
    const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
    return formatPrice(price);
  };

  const getSavings = (plan) => {
    if (plan.monthlyPrice === null || plan.monthlyPrice === 0) return null;
    const monthlyCost = plan.monthlyPrice * 12;
    const annualCost = plan.annualPrice;
    const savings = monthlyCost - annualCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return percentage;
  };

  const handleCTA = (plan) => {
    if (plan.name === 'Enterprise') {
      // Open Book Demo modal for Enterprise plan
      setShowBookDemoModal(true);
    } else if (plan.name === 'Starter') {
      navigate('/hr_login');
    } else {
      // Open Book Demo modal for paid plans
      setShowBookDemoModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white overflow-hidden py-20">
        {/* Prismatic Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
          <div className="absolute left-0 top-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
          <div className="absolute right-0 bottom-0 w-[480px] h-[480px] rounded-full bg-gradient-to-tl from-pink-500/25 via-rose-500/20 to-orange-400/15 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
            PRICING
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Start for Free, Scale with No Fees
          </h1>
          <p className="text-base sm:text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Choose the plan that works best for your needs, with no hidden fees or surprises
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-14 h-7 bg-white/20 rounded-full transition-colors hover:bg-white/30"
            >
              <div
                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${
                  billingCycle === 'annual' ? 'translate-x-7' : ''
                }`}
              ></div>
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-white' : 'text-gray-400'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="ml-2 px-2.5 py-0.5 bg-green-500/20 text-green-300 rounded-full text-xs font-semibold border border-green-500/30">
                Save 20%
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-6 shadow-md transition-all duration-300 flex flex-col group ${
                  plan.popular
                    ? 'border-2 border-[#2b6cee] shadow-xl hover:shadow-2xl hover:-translate-y-2'
                    : 'border border-gray-200 hover:shadow-xl hover:border-[#2b6cee] hover:-translate-y-2'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] text-white text-xs font-semibold rounded-full shadow-md">
                    {plan.badge}
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-xs text-gray-600 mb-4">{plan.description}</p>
                  
                  {/* Price */}
                  <div className="mb-1">
                    <span className="text-3xl font-bold text-gray-900">
                      {getPrice(plan)}
                    </span>
                    {plan.monthlyPrice !== null && plan.monthlyPrice !== 0 && (
                      <span className="text-gray-600 text-sm ml-1">/mo</span>
                    )}
                  </div>
                  
                  {/* Savings Badge */}
                  {billingCycle === 'annual' && getSavings(plan) && (
                    <div className="text-xs text-green-600 font-semibold">
                      Save {getSavings(plan)}% annually
                    </div>
                  )}
                </div>

                {/* Main Features List */}
                <ul className="space-y-2.5 mb-4 flex-grow">
                  {plan.mainFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Icon
                        name={feature.included ? 'check_circle' : 'cancel'}
                        size={16}
                        className={feature.included ? 'text-green-500 flex-shrink-0 mt-0.5' : 'text-gray-300 flex-shrink-0 mt-0.5'}
                      />
                      <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Additional Features (shown when expanded) */}
                {plan.additionalFeatures && plan.additionalFeatures.length > 0 && (
                  <>
                    <div className={`overflow-hidden transition-all duration-300 ${showAllFeatures ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <ul className="space-y-2.5 mb-4">
                        {plan.additionalFeatures.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Icon
                              name={feature.included ? 'check_circle' : 'cancel'}
                              size={16}
                              className={feature.included ? 'text-green-500 flex-shrink-0 mt-0.5' : 'text-gray-300 flex-shrink-0 mt-0.5'}
                            />
                            <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                {/* CTA Button */}
                <Button
                  onClick={() => handleCTA(plan)}
                  variant={plan.ctaVariant}
                  size="sm"
                  className={`w-full text-sm transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] text-white hover:shadow-lg group-hover:scale-105'
                      : 'group-hover:scale-105'
                  }`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>

          {/* See More Button - Centered below all cards */}
          <div className="mt-8 text-center">
            <button
              onClick={toggleAllFeatures}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-[#2b6cee] hover:text-[#1a4fb8] bg-white border-2 border-[#2b6cee] hover:border-[#1a4fb8] rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              {showAllFeatures ? (
                <>
                  <Icon name="expand_less" size={20} />
                  Show Less Features
                </>
              ) : (
                <>
                  <Icon name="expand_more" size={20} />
                  Compare All Features
                </>
              )}
            </button>
          </div>

          {/* Bottom Note */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600 mb-1">
              All plans include access to our AI platform, documentation, and community support.
            </p>
            <p className="text-sm text-gray-600">
              Need a custom solution?{' '}
              <button
                onClick={() => window.location.href = 'mailto:sales@aptirix.com?subject=Custom Solution Inquiry'}
                className="text-[#2b6cee] font-semibold hover:underline"
              >
                Contact our sales team
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-gray-600">
              Everything you need to know about our pricing
            </p>
          </div>

          <div className="space-y-3">
            {/* FAQ Item 1 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(0)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  Can I switch plans anytime?
                </h3>
                <Icon 
                  name={expandedFAQ === 0 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 0 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                </p>
              </div>
            </div>

            {/* FAQ Item 2 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(1)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  What happens if I exceed my interview limit?
                </h3>
                <Icon 
                  name={expandedFAQ === 1 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 1 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  You'll be notified when you're approaching your limit. You can either upgrade your plan or purchase additional interview credits at ₹99 per interview.
                </p>
              </div>
            </div>

            {/* FAQ Item 3 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(2)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  Is there a free trial available?
                </h3>
                <Icon 
                  name={expandedFAQ === 2 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 2 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  Yes! Professional and Business plans come with a 14-day free trial. No credit card required to start.
                </p>
              </div>
            </div>

            {/* FAQ Item 4 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(3)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  What payment methods do you accept?
                </h3>
                <Icon 
                  name={expandedFAQ === 3 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 3 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  We accept all major credit cards, debit cards, UPI, net banking, and can also process invoices for annual plans.
                </p>
              </div>
            </div>

            {/* FAQ Item 5 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(4)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  Do you offer refunds?
                </h3>
                <Icon 
                  name={expandedFAQ === 4 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 4 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  Yes, we offer a 30-day money-back guarantee for annual plans. Monthly plans can be cancelled anytime without penalty.
                </p>
              </div>
            </div>

            {/* FAQ Item 6 */}
            <div 
              className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleFAQ(5)}
            >
              <div className="p-5 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">
                  Can I add more team members?
                </h3>
                <Icon 
                  name={expandedFAQ === 5 ? 'expand_less' : 'expand_more'} 
                  size={24} 
                  className="text-[#2b6cee] flex-shrink-0 transition-transform duration-300"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-300 ${expandedFAQ === 5 ? 'max-h-40' : 'max-h-0'}`}>
                <p className="px-5 pb-5 text-sm text-gray-600">
                  Yes! Additional team members can be added to Professional and Business plans at ₹999/month per user. Enterprise plans include unlimited users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative bg-gradient-to-br from-[#0f172a] via-[#1e3a8a] to-[#0f172a] rounded-3xl overflow-hidden p-10 lg:p-12">
              {/* Prismatic Background */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
                <div className="absolute left-0 top-0 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
                <div className="absolute right-0 bottom-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
              </div>

              <div className="relative z-10 text-center max-w-2xl mx-auto">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-base sm:text-lg text-gray-200 mb-6">
                  Start your free trial today and transform your hiring process with AI
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    onClick={() => setShowBookDemoModal(true)}
                    className="bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 text-base font-semibold rounded-xl shadow-lg"
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => setShowBookDemoModal(true)}
                    className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 text-base font-semibold rounded-xl"
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Book Demo Modal */}
      <BookDemoModal 
        isOpen={showBookDemoModal} 
        onClose={() => setShowBookDemoModal(false)} 
      />
    </div>
  );
};

export default PricingPage;
